import { z } from "zod";

/**
 * El MODELO del carrito. Sin React, sin `localStorage`, sin red.
 *
 * ── Qué guarda una línea, y qué NO ───────────────────────────────────
 * `productId`, `slug` y `quantity`. NADA de precio, nombre ni imagen.
 *
 * Un precio guardado en el navegador se queda congelado el día que se
 * guardó: quien dejó el carrito abierto en enero volvería en marzo y
 * vería el precio de enero, sumaría el subtotal de enero, y el servidor
 * le cobraría el de marzo. Ese desajuste no se ve en ninguna prueba
 * automática — se ve en una reclamación. El precio se RESUELVE del
 * catálogo cada vez que se pinta (`cart.pricing.ts`), que es la única
 * fuente que puede estar al día.
 *
 * El `slug` sí se guarda aunque también viva en el catálogo: es lo que
 * permite ofrecer un enlace a la ficha de un producto que ya no está en
 * el catálogo público, en vez de una fila muerta sin salida.
 *
 * ── Por qué es todo funciones puras ──────────────────────────────────
 * Añadir, cambiar cantidad y quitar se prueban con un array y sin montar
 * nada. Los errores de un carrito (la línea que se duplica en vez de
 * sumar, la cantidad que se va a cero y deja una fila fantasma) viven
 * justo aquí.
 */

/**
 * El tope por línea.
 *
 * No es una regla de negocio, es una defensa: sin tope, un dedo apoyado
 * en el botón de "+" —o un número escrito a mano— mete 40 000 piezas y el
 * subtotal deja de tener sentido. Cuando el producto declara existencias,
 * manda la más pequeña de las dos.
 */
export const MAX_LINE_QUANTITY = 99;

/** El tope de líneas distintas. Un carrito más largo que esto es un error. */
export const MAX_CART_LINES = 50;

export const cartLineSchema = z.object({
  productId: z.string().min(1),
  slug: z.string().min(1),
  quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
});

export type CartLine = z.infer<typeof cartLineSchema>;

/**
 * Lo que de verdad viaja a `localStorage`, con su versión.
 *
 * El número de versión existe para poder cambiar la forma sin romperle el
 * carrito a quien ya tenía uno: un documento de otra versión no se
 * intenta migrar a ciegas, se descarta y se empieza limpio.
 */
export const CART_STATE_VERSION = 1;

export const cartStateSchema = z.object({
  v: z.literal(CART_STATE_VERSION),
  lines: z.array(cartLineSchema).max(MAX_CART_LINES),
});

export type CartState = z.infer<typeof cartStateSchema>;

/** Lo mínimo que hace falta para meter algo al carrito. */
export interface CartAddable {
  id: string;
  slug: string;
  /** Existencias declaradas. Si viene, topa la cantidad. */
  stock?: number | undefined;
}

/** El tope real de una línea: el del sistema, o las existencias si son menos. */
export function maxQuantityFor(stock: number | undefined): number {
  if (stock === undefined) return MAX_LINE_QUANTITY;
  return Math.max(0, Math.min(stock, MAX_LINE_QUANTITY));
}

/** Deja una cantidad dentro de `[0, max]` y en entero. `0` significa "quítala". */
export function clampQuantity(quantity: number, max = MAX_LINE_QUANTITY): number {
  if (!Number.isFinite(quantity)) return 0;
  return Math.min(Math.max(Math.trunc(quantity), 0), max);
}

/**
 * Añade — o SUMA, si el producto ya estaba.
 *
 * Sumar y no apilar una segunda línea es la diferencia entre un carrito y
 * una lista: dos filas del mismo producto obligan a quien compra a hacer
 * la suma de cabeza, y a quien programa a decidir cuál de las dos borra
 * el botón de quitar.
 *
 * La línea que ya existía CONSERVA su posición. Moverla al final hace que
 * el artículo desaparezca de debajo del cursor justo cuando alguien está
 * pulsando "+" sobre él.
 */
export function addLine(
  lines: readonly CartLine[],
  product: CartAddable,
  quantity = 1,
  max = maxQuantityFor(product.stock),
): CartLine[] {
  const wanted = clampQuantity(quantity, max);
  if (wanted < 1) return [...lines];

  const existing = lines.find((line) => line.productId === product.id);

  if (existing) {
    return lines.map((line) =>
      line.productId === product.id
        ? { ...line, slug: product.slug, quantity: clampQuantity(line.quantity + wanted, max) }
        : line,
    );
  }

  if (lines.length >= MAX_CART_LINES) return [...lines];

  return [...lines, { productId: product.id, slug: product.slug, quantity: wanted }];
}

/** Fija la cantidad de una línea. `0` la quita — no deja una fila en cero. */
export function setLineQuantity(
  lines: readonly CartLine[],
  productId: string,
  quantity: number,
  max = MAX_LINE_QUANTITY,
): CartLine[] {
  const next = clampQuantity(quantity, max);
  if (next < 1) return removeLine(lines, productId);
  return lines.map((line) =>
    line.productId === productId ? { ...line, quantity: next } : line,
  );
}

export function removeLine(
  lines: readonly CartLine[],
  productId: string,
): CartLine[] {
  return lines.filter((line) => line.productId !== productId);
}

/**
 * Cuántos ARTÍCULOS hay — no cuántas líneas.
 *
 * Es lo que dice el contador de la barra superior: tres frascos del mismo
 * colágeno son tres cosas que van a llegar en la caja, y enseñar "1"
 * porque es una sola línea hace dudar de si el carrito registró los otros
 * dos.
 */
export function countItems(lines: readonly CartLine[]): number {
  return lines.reduce((total, line) => total + line.quantity, 0);
}
